/**
 * My Orders — brand-specific hub.
 *
 * Mounted at `/probound/my-orders`, `/proepoxy/my-orders`, `/prodecor/my-orders`.
 * Replaces the three flat tiles that used to live on each brand's main hub
 * (Build a new order / Pricing & Orders / My Orders) so the brand hub
 * stays uncluttered — installers come here for everything order-related.
 *
 * Tiles:
 *   • Build a new order   → the brand's existing materials-order builder
 *   • Pricing & Orders    → /installer/material-prices?brand=…
 *   • My Orders           → the brand's existing orders list page
 */
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Home, ShoppingCart, Tag, Truck } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const BRAND_CONFIG = {
  probound: {
    label: 'ProBound',
    backTo: '/probound',
    headerBg: 'linear-gradient(135deg,#0B0B0E 0%,#1f4030 75%)',
    accent: '#D4A24B',
    paths: {
      build: '/order',
      pricing: '/installer/material-prices?brand=probound',
      orders: '/probound/orders',
    },
  },
  proepoxy: {
    label: 'ProEpoxy',
    backTo: '/proepoxy',
    headerBg: 'linear-gradient(135deg,#0B0B0E 0%,#5b21b6 80%)',
    accent: '#a78bfa',
    paths: {
      build: '/proepoxy/order',
      pricing: '/installer/material-prices?brand=proepoxy',
      orders: '/proepoxy/orders',
    },
  },
  prodecor: {
    label: 'ProDecor',
    backTo: '/prodecor',
    headerBg: 'linear-gradient(135deg,#0B0B0E 0%,#9a3412 80%)',
    accent: '#fb923c',
    paths: {
      build: '/prodecor/calculator',
      pricing: '/installer/material-prices?brand=prodecor',
      orders: '/prodecor/orders',
    },
  },
};

export default function MyOrdersHub() {
  const navigate = useNavigate();
  // Mounted at `/probound/my-orders`, `/proepoxy/my-orders`, `/prodecor/my-orders`
  // — brand is the first path segment, NOT a route param.
  const { pathname } = useLocation();
  const brandFromPath = pathname.split('/').filter(Boolean)[0];
  const brand = BRAND_CONFIG[brandFromPath] ? brandFromPath : 'probound';
  const conf = BRAND_CONFIG[brand];

  const TILES = [
    {
      id: 'build',
      title: 'Build a new order',
      description: `Build & submit a fresh ${conf.label} materials order.`,
      icon: ShoppingCart,
      path: conf.paths.build,
      accent: '#2F5E96',
    },
    {
      id: 'pricing',
      title: 'Price Lists',
      description: 'RRP, your tier price · build & submit material orders.',
      icon: Tag,
      path: conf.paths.pricing,
      accent: conf.accent,
    },
    {
      id: 'orders',
      title: 'Material Orders',
      description: 'Drafts, submitted & completed orders.',
      icon: Truck,
      path: conf.paths.orders,
      accent: '#1f7a4a',
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div
        className="text-white"
        style={{ background: conf.headerBg, borderBottom: `3px solid ${conf.accent}` }}
      >
        <div className="max-w-[1100px] mx-auto px-3 sm:px-4 py-4 sm:py-5 flex items-center gap-2 sm:gap-3">
          <button
            onClick={() => navigate(-1)}
            data-testid="myorders-back"
            className="p-2 hover:bg-white/10 rounded-lg shrink-0"
            aria-label="Back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-2xl font-bold flex items-center gap-2 truncate">
              <Truck className="w-5 h-5 sm:w-6 sm:h-6 shrink-0" style={{ color: conf.accent }} />
              Material Orders — {conf.label}
            </h1>
            <p className="hidden sm:block text-white/70 text-sm mt-0.5">
              Everything order-related for {conf.label} in one place.
            </p>
          </div>
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            size="sm"
            className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0 px-2 sm:px-3"
          >
            <Home className="w-4 h-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Home</span>
          </Button>
        </div>
      </div>

      <div className="max-w-[1100px] mx-auto px-3 sm:px-4 py-6 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        {TILES.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => navigate(t.path)}
              data-testid={`myorders-${t.id}`}
              className="text-left"
            >
              <Card className="p-5 h-full hover:shadow-lg transition-all border-2 hover:border-gray-300">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-3"
                  style={{ background: t.accent + '22', color: t.accent }}
                >
                  <Icon className="w-6 h-6" />
                </div>
                <h2 className="text-base font-bold text-gray-900 mb-1">{t.title}</h2>
                <p className="text-xs text-gray-500">{t.description}</p>
              </Card>
            </button>
          );
        })}
      </div>
    </div>
  );
}
